import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, logger, resetMemberPassword, validateMemberPasswordResetCode } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useFormValidator } from '../../../hooks/use-form-validator';
import { Form } from '../../../subcomponents/form/form/form';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { MemberPasswordResetFormMessage } from './subcomponents/message';
import { MemberPasswordResetFormContextInstance } from './types/context';
import { defaultMemberPasswordResetFormData } from './types/data';
import { defaultMemberPasswordResetFormProps, memberPasswordResetFormPropsValidator } from './types/props';
import { getNotificationDetailsForError } from './utils/get-notification-details-for-error';
import { getValidationConfig } from './utils/get-validation-config';
const b = bemClassBuilder('v-member-password-reset-form');
const MemberPasswordResetFormBase = (props, ref) => {
    const { form: formConfig } = useOccConfig();
    const { displayText, events, children, resetCode, formId } = props;
    const [formData, setFormData] = useState(defaultMemberPasswordResetFormData);
    const validationConfig = getValidationConfig(formConfig.fieldValidation);
    const { validationResult, validator } = useFormValidator(validationConfig);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState();
    const [resetCodeValidity, setResetCodeValidity] = useState();
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const result = await validateMemberPasswordResetCode(resetCode);
                setResetCodeValidity(result);
            }
            catch (error) {
                logger.error(error);
                setApiError(error);
            }
            finally {
                setIsLoading(false);
            }
        })();
    }, [resetCode]);
    const context = useMemo(() => ({
        onChange: (fieldName, value) => setFormData(data => (Object.assign(Object.assign({}, data), { [fieldName]: value }))),
        formData,
        validationResult,
        validationConfig,
        validator,
        disabled: isSubmitting,
        loading: isLoading
    }), [formData, validationResult, isLoading, isSubmitting, validationConfig, validator]);
    const handleSubmit = async () => {
        await resetMemberPassword(formData.password, resetCode);
    };
    const handleFailedPasswordReset = error => {
        var _a;
        addErrorNotification(getNotificationDetailsForError(displayText, error), error);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events);
    };
    const handleFormSubmittingStateChanged = (submitting) => {
        var _a;
        setIsSubmitting(submitting);
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, submitting);
    };
    const isResetCodeValid = resetCodeValidity && resetCodeValidity.isValid;
    if (isResetCodeValid === false || apiError) {
        return _jsx(MemberPasswordResetFormMessage, { displayText: displayText, events: events });
    }
    return (_jsx(Form, Object.assign({}, props, { id: formId, className: b.block(), validator: () => validator.validateForm(formData), isLoading: isLoading, events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmit, onAfterFailedSubmit: handleFailedPasswordReset, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), setFormData: setFormData, ref: ref }, { children: _jsx(MemberPasswordResetFormContextInstance.Provider, Object.assign({ value: context }, { children: children })) })));
};
const MemberPasswordResetForm = observer(forwardRef(MemberPasswordResetFormBase));
MemberPasswordResetForm.displayName = 'MemberPasswordResetForm';
/**
 * The **Member Password Reset Form** component allows a customer to reset their password for their loyalty account.
 */
export const MemberPasswordResetFormComponent = withComponentWrapper(MemberPasswordResetForm, defaultMemberPasswordResetFormProps, memberPasswordResetFormPropsValidator);
